import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as S from './ScrollSpy.styles';

export interface ScrollSpyItem {
  id: string;
  label: string;
  ref: React.RefObject<HTMLDivElement | null>;
}

export interface ScrollSpyProps {
  items: ScrollSpyItem[];
  className?: string;
  containerRef?: React.RefObject<HTMLElement | null>;
}

export const ScrollSpy = ({
  items,
  className,
  containerRef,
}: ScrollSpyProps) => {
  const [activeItem, setActiveItem] = useState<string | null>(
    items[0]?.id || null
  );
  const activeItemRef = useRef<string | null>(activeItem);
  const isScrollingProgrammatically = useRef(false);

  const handleScroll = useCallback(() => {
    // Skip scroll handling if we're programmatically scrolling
    if (isScrollingProgrammatically.current) {
      return;
    }

    const container = containerRef?.current;

    if (!container) {
      return;
    }

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const containerHeight = container.offsetHeight;

    // Calculate scroll progress as percentage (0-100%)
    const scrollProgress =
      scrollHeight > containerHeight
        ? (scrollTop / (scrollHeight - containerHeight)) * 100
        : 0;

    let currentActiveItem = null;

    // Calculate each item's position range in the scrollable content
    const itemPositions = items
      .map(item => {
        const element = item.ref.current;

        if (!element) {
          return null;
        }

        const elementOffsetTop = element.offsetTop;
        const elementHeight = element.offsetHeight;
        const elementBottom = elementOffsetTop + elementHeight;

        // Calculate item's position as percentage of total scrollable content
        const itemStartPercent = (elementOffsetTop / scrollHeight) * 100;
        const itemEndPercent = (elementBottom / scrollHeight) * 100;

        return {
          id: item.id,
          startPercent: itemStartPercent,
          endPercent: itemEndPercent,
          elementOffsetTop,
          elementHeight,
        };
      })
      .filter(Boolean);

    for (const position of itemPositions) {
      if (!position) {
        continue;
      }

      // Check if current scroll progress falls within this item's range
      const tolerance = 5;
      const adjustedStartPercent = Math.max(
        0,
        position.startPercent - tolerance
      );

      if (
        scrollProgress >= adjustedStartPercent &&
        scrollProgress < position.endPercent
      ) {
        currentActiveItem = position.id;
        break;
      }
    }

    if (!currentActiveItem && itemPositions.length > 0) {
      const firstItem = itemPositions[0];
      const lastItem = itemPositions[itemPositions.length - 1];

      // If we're at the very beginning, activate first item
      if (scrollProgress <= 5 && firstItem) {
        currentActiveItem = firstItem.id;
      }
      // If we're at the very end, activate last item
      else if (scrollProgress >= 95 && lastItem) {
        currentActiveItem = lastItem.id;
      }
    }

    if (currentActiveItem && currentActiveItem !== activeItemRef.current) {
      activeItemRef.current = currentActiveItem;
      setActiveItem(currentActiveItem);
    }
  }, [items, containerRef]);

  const handleItemClick = (itemId: string) => {
    const item = items.find(item => item.id === itemId);
    const element = item?.ref.current;
    const container = containerRef?.current;

    if (element && container) {
      // Set flag to prevent scroll handler from running during programmatic scroll
      isScrollingProgrammatically.current = true;

      setActiveItem(itemId);
      activeItemRef.current = itemId;

      const elementOffsetTop = element.offsetTop;

      container.scrollTo({
        top: Math.max(0, elementOffsetTop - 150),
        behavior: 'instant',
      });

      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 1500);
    }
  };

  useEffect(() => {
    const container = containerRef?.current;

    if (!container) {
      return;
    }

    container.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <S.MenuContainer className={className} data-testid="scroll-spy-container">
      {items.map(item => (
        <S.ItemWrapper
          key={item.id}
          $active={activeItem === item.id}
          onClick={() => handleItemClick(item.id)}
          data-testid={`scroll-spy-item-${item.id}`}
          className={activeItem === item.id ? 'active' : ''}
        >
          <S.Item $active={activeItem === item.id}>{item.label}</S.Item>
        </S.ItemWrapper>
      ))}
    </S.MenuContainer>
  );
};
